/**
 * One body cell of the Home doc-list table, rendered once per visible column per
 * virtual row by `DocListTable`. A cell is pinned to the left edge exactly when
 * `frozenLeftOffset` is defined — `getFrozenLeftOffsets` decides that, and this
 * component asks nothing else.
 *
 * Stacking invariant: the pinned cell's `zIndex` is local to its row because
 * `DocListTable` gives every row a `transform: translateY`, which makes the row
 * its own stacking context. Swap that `transform` for `top` and the row stops
 * being a stacking context, so the pinned column paints through the sticky
 * header (z-index 2) instead of beneath it.
 *
 * `spanAll` stretches the cell across every track — the card title line. It sets
 * the `gridColumn` shorthand only when true, so table mode keeps auto-placement.
 * `cardMode` drops the cell background so the card's own background shows through.
 */

import { FC, memo, ReactNode } from 'react';
import { theme } from 'antd';

interface DocListTableCellProps {
  children: ReactNode;
  frozenLeftOffset?: number;
  spanAll?: boolean;
  cardMode?: boolean;
}

const DocListTableCell: FC<DocListTableCellProps> = ({ children, frozenLeftOffset, spanAll = false, cardMode = false }) => {
  const { token } = theme.useToken();
  const isFrozen = frozenLeftOffset !== undefined;

  return (
    <div
      style={{
        maxHeight: token.controlHeight,
        display: 'flex',
        alignItems: 'center',
        backgroundColor: cardMode ? 'transparent' : token.colorBgBase,
        color: token.colorText,
        boxSizing: 'border-box',
        minWidth: 0,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        width: '100%',
        flex: 1,
        padding: `${token.paddingXS}px ${token.paddingSM}px`,
        position: isFrozen ? 'sticky' : 'relative',
        left: isFrozen ? `${frozenLeftOffset}px` : undefined,
        zIndex: isFrozen ? 10 : 1,
        boxShadow: isFrozen ? `inset -1px 0 0 ${token.colorBorder}` : undefined,
        gridColumn: spanAll ? '1 / -1' : undefined,
      }}
    >
      {children}
    </div>
  );
};

export default memo(DocListTableCell);
